import { useJumboDialog } from '@jumbo/components/JumboDialog/hooks/useJumboDialog';
import useJumboAuth from '@jumbo/hooks/useJumboAuth';
import ArticleIcon from '@mui/icons-material/Article';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { Button, Pagination } from '@mui/material';
import { DataGridPro, GridToolbar } from '@mui/x-data-grid-pro';
import { NavLink } from 'react-router-dom';
import { OperationalExpensesCleanUp, operationalExpenses } from 'app/redux/actions/subTaskAction';
import subTaskService from 'app/services/subtask_service';
import moment from 'moment';
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Swal from 'sweetalert2';
import ExpenseDocumentBox from './ExpenseDocumentBox';
import OperationalExpenseForm from './OperationalExpenseForm';
import OpPaymentStatusForm from './OpPaymentStatusForm';

// Define the limit for pagination
const LIMIT = 10;

export default function OperationalExpense() {
  const dispatch = useDispatch();
  const { setAuthToken } = useJumboAuth();
  const roleCode = localStorage.getItem('roleCode');

  const { operationalExpensesData } = useSelector((state) => state.typereducer);

  // Pagination & Filtering State
  const [page, setPage] = React.useState(1);
  const [offset, setOffset] = React.useState(0);
  const [totalCount, setTotalCount] = React.useState(0);
  const [queryOptions, setQueryOptions] = React.useState({});
  const [isLoading, setIsLoading] = React.useState(false); // To handle loading state

  const { hideDialog, showDialog } = useJumboDialog();

  // Utility to generate row IDs
  const [pageId, setPageId] = React.useState(1);
  var _id = pageId;

  const rows = React.useMemo(() =>
    operationalExpensesData.map((item) => ({
      id: _id++, // Using pre-increment in the map
      vendorName: item?.vendor_id?.vendor_name,
      tdsPercent: item?.vendor_id?.tds_amount,
      expenseId: item?.id,
      expenseAmount: item?.expense_amount,
      expenseDate: item?.expense_date ? moment(item?.expense_date).format('DD MMM YYYY') : '',
      expenseDescription: item?.expense_description || '--',
      expenseNote: item?.expense_note || '--',
      expenseDoc: item?.bill_reciept_documents || [], // Ensure this is an array for safety
      status: item?.status || 'Requested',
      hrFinanceStatus: item?.hr_finance_status || '--',
      paymentStatus: item?.payment_status || 'Unpaid',
      paymentDate: item?.payment_date ? moment(item?.payment_date).format('DD MMM YYYY') : '',
      actionBy: item?.action_by ? item?.action_by.name : '--',
      addedBy: item?.created_by ? item?.created_by.name : '--',
      actionRemark: item?.action_remark || '--',
      sgstAmount: item?.sgst_amount || '--',
      cgstAmount: item?.cgst_amount || '--',
      igstAmount: item?.igst_amount || '--',
      tdsAmount: item?.tds_amount || '--',
      totalGST: item?.total_gst || '--',
      amountWithTax: item?.amount_with_tax || '--',
      payableAmount: item?.payable_amount || '--',
      invoiceNumber: item?.invoice_number || '--',
      company: item?.company || '--',
      projects: item?.projects || '--',
    })), [operationalExpensesData]);

  // --- Utility Functions ---
  const handlePageChange = (event, value) => {
    setPage(value);
    setOffset((value - 1) * LIMIT);
    setPageId((value - 1) * LIMIT + 1);
  };

  const showExpenseDialog = (expenseDoc) => {
    showDialog({
      title: 'Expense Documents',
      content: <ExpenseDocumentBox expenseDoc={expenseDoc} />,
    });
  };

  const sweetAlerts = (variant, message, callback) => {
    Swal.fire({
      icon: variant,
      title: variant === 'success' ? 'Success' : 'Warning',
      text: message,
      showCancelButton: variant !== 'success',
      confirmButtonText: 'OK',
      cancelButtonText: 'Cancel',
    }).then((result) => {
      if (result.isConfirmed && callback) {
        callback();
      }
    });
  };

  const cancleExpenseRequest = (expanseId) => {
    sweetAlerts('warning', 'Are you sure you want to cancel this expense?', () => {
      let payload = {
        status: 'Cancelled',
      };
      subTaskService
        .updateOpExpenseStatus(payload, expanseId)
        .then((data) => {
          hideDialog();
          sweetAlerts('success', 'Expense Cancelled Successfully!', () => {
            // Reload data after successful cancellation
            fetchOperationalExpenses(offset, LIMIT, getFilterString(queryOptions));
          });
        })
        .catch((err) => {
          hideDialog();
          if (err?.response?.status === 401) {
            setAuthToken(null);
          }
          sweetAlerts('error', 'Failed to Cancel expense');
        });
    });
  };

  const handleCellClick = (params) => {
    const columnId = params.field;
    if (columnId !== 'id' && columnId !== '__check__' && columnId !== 'expenseDoc') {
      const cellValue = params.value;
      Swal.fire({
        html: `<div style="white-space: pre-wrap;">${cellValue}</div>`,
        showConfirmButton: false,
        customClass: {
          popup: 'my-custom-size',
        },
        didOpen: () => {
          const swalPopup = Swal.getPopup();
          if (swalPopup) {
            swalPopup.style.width = '400px';
          }
        },
      });
    }
  };

  // --- Dialog Handlers ---
  const handleDataChange = React.useCallback(() => {
    hideDialog();
    // Refresh data after form submission (add/edit) or payment status update
    fetchOperationalExpenses(offset, LIMIT, getFilterString(queryOptions));
  }, [hideDialog, offset, queryOptions]);


  const showAddExpenseDialog = React.useCallback(
    (expanseId) => {
      showDialog({
        title: expanseId ? 'Edit Operational Expense' : 'Add Operational Expense',
        content: <OperationalExpenseForm expanseId={expanseId} onSave={handleDataChange} />,
      });
    },
    [handleDataChange, showDialog]
  );

  const showPaymentStatusDialog = React.useCallback(
    (expanseId, paymentStatus, paymentDate, hrFinanceStatus) => {
      showDialog({
        title: 'Update Payment Status',
        content: (
          <OpPaymentStatusForm
            expanseId={expanseId}
            paymentStatus={paymentStatus}
            paymentDate={paymentDate}
            hrFinanceStatus={hrFinanceStatus}
            onSave={handleDataChange}
          />
        ),
      });
    },
    [handleDataChange, showDialog]
  );

  // --- Data Fetching and Filtering Logic ---
  const getFilterString = (options) => {
    if (options?.filterModel?.items) {
      return options.filterModel.items
        .filter((item) => item.field !== undefined && item.value !== undefined && item.value !== null && item.value !== '')
        .map((item) => `&${item.field}=${item.value}`)
        .join('');
    }
    return '';
  };

  const onFilterChange = React.useCallback((filterModel) => {
    // Reset to page 1 and offset 0 on new filter application
    setOffset(0);
    setPage(1);
    setPageId(1);
    setQueryOptions({ filterModel: { ...filterModel } });
  }, []);

  const fetchOperationalExpenses = React.useCallback(async (currentOffset, currentLimit, filterStr) => {
    setIsLoading(true);
    try {
      const data = await subTaskService.getOperationalExpenses(currentOffset, currentLimit, filterStr);
      setTotalCount(data?.data?.count || 0);
      dispatch(operationalExpenses(data?.data?.data || []));
    } catch (err) {
      if (err?.response?.status === 401) {
        setAuthToken(null);
      }
      console.error('Failed to fetch operational expenses:', err);
      dispatch(operationalExpenses([]));
      setTotalCount(0);
    } finally {
      setIsLoading(false);
    }
  }, [dispatch, setAuthToken]);


  // Effect to fetch data whenever offset or queryOptions changes
  React.useEffect(() => {
    const filterString = getFilterString(queryOptions);
    fetchOperationalExpenses(offset, LIMIT, filterString);

    return () => {
      dispatch(OperationalExpensesCleanUp());
    };
  }, [offset, queryOptions, fetchOperationalExpenses, dispatch]);


  // --- DataGrid Columns Definition ---
  const columns = React.useMemo(() => [
    {
      field: 'id',
      headerName: 'ID',
      sortable: false,
      hideable: false,
      filterable: false,
      flex: 100,
      maxWidth: 100,
      minWidth: 80,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        const isEditable = params.row.status !== 'Cancelled' && params.row.status !== 'Approved' && params.row.status !== 'Rejected';
        const isDeletable = params.row.status === 'Requested';
        return (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <span>{params.value}</span>
            <NavLink>
              <EditIcon
                onClick={() => isEditable && showAddExpenseDialog(params.row.expenseId)}
                style={{
                  width: '20px',
                  height: '20px',
                  marginLeft: '5px',
                  cursor: isEditable ? 'pointer' : 'not-allowed',
                }}
                color="action"
                disabled={!isEditable}
              />
            </NavLink>
            <NavLink>
              <DeleteIcon
                onClick={() => isDeletable && cancleExpenseRequest(params.row.expenseId)}
                style={{
                  width: '20px',
                  height: '20px',
                  marginLeft: '5px',
                  cursor: isDeletable ? 'pointer' : 'not-allowed',
                }}
                color="action"
                disabled={!isDeletable}
              />
            </NavLink>
          </div>
        );
      },
      renderHeader: () => (
        <div style={{ whiteSpace: 'normal', wordWrap: 'break-word', textAlign: 'center', lineHeight: '1', marginTop: '2px' }}>
          <span>ID</span>
        </div>
      ),
    },
    {
      field: 'vendorName',
      headerName: 'Vendor Name',
      sortable: false,
      flex: 300,
      align: 'center',
      headerAlign: 'center',
      renderHeader: () => (
        <div style={{ whiteSpace: 'normal', wordWrap: 'break-word', textAlign: 'center', lineHeight: '1', marginTop: '2px' }}>
          <span>Vendor Name</span>
        </div>
      ),
    },
    {
      field: 'status',
      headerName: 'Manager Status',
      sortable: false,
      flex: 220,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        let chipColor = 'gray';
        switch (params.value) {
          case 'Requested':
            chipColor = 'blue';
            break;
          case 'Approved':
            chipColor = 'green';
            break;
          case 'Rejected':
            chipColor = 'red';
            break;
          case 'Cancelled':
            chipColor = 'purple';
            break;
          default:
            chipColor = 'gray';
        }

        return (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <div
              style={{
                width: 10,
                height: 10,
                backgroundColor: chipColor,
                marginRight: 8,
                borderRadius: '100%',
              }}
            ></div>
            {params.value}
          </div>
        );
      },
      renderHeader: () => (
        <div style={{ whiteSpace: 'normal', wordWrap: 'break-word', textAlign: 'center', lineHeight: '1', marginTop: '2px' }}>
          <span>Manager Status</span>
        </div>
      ),
    },
    {
      field: 'invoiceNumber',
      headerName: 'Invoice No.',
      sortable: false,
      filterable: false,
      flex: 220,
      align: 'center',
      headerAlign: 'center',
      renderHeader: () => (
        <div style={{ whiteSpace: 'normal', wordWrap: 'break-word', textAlign: 'center', lineHeight: '1', marginTop: '2px' }}>
          <span>Invoice No.</span>
        </div>
      ),
    },
    {
      field: 'addedBy',
      headerName: 'Added By',
      sortable: false,
      filterable: false,
      flex: 220,
      align: 'center',
      headerAlign: 'center',
      renderHeader: () => (
        <div style={{ whiteSpace: 'normal', wordWrap: 'break-word', textAlign: 'center', lineHeight: '1', marginTop: '2px' }}>
          <span>Added By</span>
        </div>
      ),
    },
    {
      field: 'actionRemark',
      headerName: 'Remark',
      filterable: false,
      sortable: false,
      flex: 300,
      align: 'center',
      headerAlign: 'center',
      renderHeader: () => (
        <div style={{ whiteSpace: 'normal', wordWrap: 'break-word', textAlign: 'center', lineHeight: '1', marginTop: '2px' }}>
          <span>Remark</span>
        </div>
      ),
    },
    {
      field: 'actionBy',
      headerName: 'Approved By',
      sortable: false,
      filterable: false,
      flex: 220,
      align: 'center',
      headerAlign: 'center',
      renderHeader: () => (
        <div style={{ whiteSpace: 'normal', wordWrap: 'break-word', textAlign: 'center', lineHeight: '1', marginTop: '2px' }}>
          <span>Approved By</span>
        </div>
      ),
    },
    {
      field: 'expenseAmount',
      headerName: 'Expense Amount',
      sortable: false,
      flex: 220,
      align: 'center',
      headerAlign: 'center',
      renderHeader: () => (
        <div style={{ whiteSpace: 'normal', wordWrap: 'break-word', textAlign: 'center', lineHeight: '1', marginTop: '2px' }}>
          <span>Expense Amount</span>
        </div>
      ),
    },
    {
      field: 'expenseDate',
      headerName: 'Expense Date',
      sortable: false,
      filterable: false,
      flex: 220,
      align: 'center',
      headerAlign: 'center',
      renderHeader: () => (
        <div style={{ whiteSpace: 'normal', wordWrap: 'break-word', textAlign: 'center', lineHeight: '1', marginTop: '2px' }}>
          <span>Expense Date</span>
        </div>
      ),
    },
    {
      field: 'expenseDescription',
      headerName: 'Description',
      filterable: false,
      sortable: false,
      flex: 300,
      align: 'center',
      headerAlign: 'center',
      renderHeader: () => (
        <div style={{ whiteSpace: 'normal', wordWrap: 'break-word', textAlign: 'center', lineHeight: '1', marginTop: '2px' }}>
          <span>Description</span>
        </div>
      ),
    },
    {
      field: 'expenseNote',
      headerName: 'Note',
      filterable: false,
      sortable: false,
      flex: 300,
      align: 'center',
      headerAlign: 'center',
      renderHeader: () => (
        <div style={{ whiteSpace: 'normal', wordWrap: 'break-word', textAlign: 'center', lineHeight: '1', marginTop: '2px' }}>
          <span>Note</span>
        </div>
      ),
    },
    {
      field: 'paymentStatus',
      headerName: 'Payment Status',
      type: 'singleSelect',
      valueOptions: ['Paid', 'Unpaid'],
      sortable: false,
      flex: 300,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        // Check if the user role is authorized AND the expense is approved
        const isAuthorizedToEdit =
          params.row.hrFinanceStatus === 'Approved' && ['SUPERADMIN', 'FINANCEMANAGER', 'ACCOUNTSMANAGER'].includes(roleCode);

        return (
          <Button
            size="small"
            onClick={() =>
              isAuthorizedToEdit && showPaymentStatusDialog(
                params.row.expenseId,
                params.row.paymentStatus,
                params.row.paymentDate,
                params.row.hrFinanceStatus
              )
            }
            disabled={!isAuthorizedToEdit}
            style={{
              cursor: isAuthorizedToEdit ? 'pointer' : 'not-allowed',
              textTransform: 'none' // Keep the text case as is
            }}
          >
            {params.value}
          </Button>
        );
      },
      renderHeader: () => (
        <div style={{ whiteSpace: 'normal', wordWrap: 'break-word', textAlign: 'center', lineHeight: '1', marginTop: '2px' }}>
          <span>Payment Status</span>
        </div>
      ),
    },
    {
      field: 'paymentDate',
      headerName: 'Payment Date',
      type: 'date',
      sortable: false,
      flex: 300,
      align: 'center',
      headerAlign: 'center',
      valueGetter: (params) => {
        const date = params.value ? moment(params.value, 'DD MMM YYYY').toDate() : null;
        return date;
      },
      renderHeader: () => (
        <div style={{ whiteSpace: 'normal', wordWrap: 'break-word', textAlign: 'center', lineHeight: '1', marginTop: '2px' }}>
          <span>Payment Date</span>
        </div>
      ),
    },
    {
      field: 'tdsPercent',
      headerName: 'TDS Percent',
      filterable: false,
      sortable: false,
      flex: 300,
      align: 'center',
      headerAlign: 'center',
      renderHeader: () => (
        <div style={{ whiteSpace: 'normal', wordWrap: 'break-word', textAlign: 'center', lineHeight: '1', marginTop: '2px' }}>
          <span>TDS Percent</span>
        </div>
      ),
    },
    {
      field: 'tdsAmount',
      headerName: 'TDS Amount',
      filterable: false,
      sortable: false,
      flex: 300,
      align: 'center',
      headerAlign: 'center',
      renderHeader: () => (
        <div style={{ whiteSpace: 'normal', wordWrap: 'break-word', textAlign: 'center', lineHeight: '1', marginTop: '2px' }}>
          <span>TDS Amount</span>
        </div>
      ),
    },
    {
      field: 'totalGST',
      headerName: 'Total GST',
      filterable: false,
      sortable: false,
      flex: 300,
      align: 'center',
      headerAlign: 'center',
      renderHeader: () => (
        <div style={{ whiteSpace: 'normal', wordWrap: 'break-word', textAlign: 'center', lineHeight: '1', marginTop: '2px' }}>
          <span>Total GST</span>
        </div>
      ),
    },
    {
      field: 'amountWithTax',
      headerName: 'Amount with tax',
      filterable: false,
      sortable: false,
      flex: 300,
      align: 'center',
      headerAlign: 'center',
      renderHeader: () => (
        <div style={{ whiteSpace: 'normal', wordWrap: 'break-word', textAlign: 'center', lineHeight: '1', marginTop: '2px' }}>
          <span>Amount with tax</span>
        </div>
      ),
    },
    {
      field: 'payableAmount',
      headerName: 'Payable Amount',
      filterable: false,
      sortable: false,
      flex: 300,
      align: 'center',
      headerAlign: 'center',
      renderHeader: () => (
        <div style={{ whiteSpace: 'normal', wordWrap: 'break-word', textAlign: 'center', lineHeight: '1', marginTop: '2px' }}>
          <span>Payable Amount</span>
        </div>
      ),
    },
    {
      field: 'projects', headerName: 'Projects', filterable: true, sortable: false, flex: 300, align: 'center', headerAlign: 'center', renderHeader: () => (
        <div style={{ whiteSpace: 'normal', wordWrap: 'break-word', textAlign: 'center', lineHeight: '1', marginTop: '2px' }}>
          <span>Projects</span>
        </div>
      ),
    },
    {
      field: 'company', headerName: 'Company', filterable: true, sortable: false, flex: 300, align: 'center', headerAlign: 'center', renderHeader: () => (
        <div style={{ whiteSpace: 'normal', wordWrap: 'break-word', textAlign: 'center', lineHeight: '1', marginTop: '2px' }}>
          <span>Company</span>
        </div>
      ),
    },
    {
      field: 'expenseDoc',
      headerName: 'Documents',
      filterable: false,
      sortable: false,
      flex: 300,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {params.value && params.value.length > 0 && (
            <ArticleIcon
              onClick={() => showExpenseDialog(params.value)}
              style={{ width: '20px', height: '20px', marginLeft: '5px', cursor: 'pointer' }}
              color="action"
            />
          )}
        </div>
      ),
      renderHeader: () => (
        <div style={{ whiteSpace: 'normal', wordWrap: 'break-word', textAlign: 'center', lineHeight: '1', marginTop: '2px' }}>
          <span>Documents</span>
        </div>
      ),
    },
  ], [showAddExpenseDialog, showPaymentStatusDialog, cancleExpenseRequest, roleCode]);

  return (
    <>
      <div style={{ height: '60px', width: '100%' }}>
        <Button sx={{ float: 'right' }} variant="contained" onClick={() => showAddExpenseDialog()}>
          Add Expense
        </Button>
      </div>
      <div style={{ height: '100%', width: '100%' }}>
        <DataGridPro
          sx={{ backgroundColor: '#F6F4FA' }}
          rows={rows}
          columns={columns}
          loading={isLoading}
          pageSize={LIMIT}
          autoPageSize={true}
          autoHeight={true}
          density="standard"
          filterMode="server"
          onFilterModelChange={onFilterChange}
          hideFooter={true}
          onCellClick={(params, event) => handleCellClick(params, event)}
        // components={{ Toolbar: GridToolbar }} // Include if you want the default toolbar
        />
        <Pagination
          count={Math.ceil(totalCount / LIMIT)}
          sx={{ mt: 3, float: 'right', color: '#F6F4FA' }}
          page={page}
          onChange={handlePageChange}
        />
      </div>
    </>
  );
}